/**
 * Sold-out product cards: turn the disabled "Sold out" button into a STOQ
 * (Restock Rocket) "Notify me" action. Ported from ET's etr-card-restock.js.
 *
 * Cards opt in with data-bn-card-restock="<product id>" plus
 * data-bn-restock-url and data-bn-restock-title (product-grid-item,
 * bn-product-carousel, for-you-product-card). Until STOQ is ready, or if it
 * is switched off, the button stays exactly as rendered: disabled "Sold out".
 * Availability is re-checked on click, so a card from a stale page never
 * hides a variant that is back in stock. No stock writes or cart events.
 */
(() => {
  'use strict';
  if (window.BNCardRestock) return;
  const selector = '[data-bn-card-restock]';
  const label = (document.currentScript && document.currentScript.dataset.notifyLabel) || 'Notify me';
  const originals = new WeakMap();
  const pending = new WeakSet();
  let enabled = true;
  let attempts = 0;
  let timer;
  let scriptRequested = false;
  const ready = () => enabled && typeof (window._RestockRocket && window._RestockRocket.openModal) === 'function';

  function restore(button) {
    const original = originals.get(button);
    if (!original) return;
    button.replaceChildren(...original.nodes.map(node => node.cloneNode(true)));
    button.className = original.className;
    button.disabled = original.disabled;
    for (const [name, value] of Object.entries(original.attributes)) {
      if (value === null) button.removeAttribute(name);
      else button.setAttribute(name, value);
    }
    button.removeAttribute('data-bn-restock-ready');
    originals.delete(button);
  }

  // The embed loads a STOQ build on collection and product pages. Elsewhere
  // (home, search, For You) load the same collection build for these cards
  // only, once the app is enabled and an eligible card exists.
  function ensureApp() {
    const config = window._RestockRocketConfig;
    const settings = config && (config.settings || config.cachedSettings);
    if (ready() || scriptRequested || !enabled || !settings) return;
    if (settings.enable_app !== true || !document.querySelector(selector)) return;
    const source = config.scriptUrlCollection;
    if (!source) return;
    const url = new URL(source, location.href);
    if (url.origin !== 'https://cdn.shopify.com' || !url.pathname.startsWith('/extensions/')) return;
    scriptRequested = true;
    const host = url.href.slice(0, url.href.lastIndexOf('/') + 1);
    if ([...document.scripts].some(script => script.src.startsWith(host))) return;
    const script = document.createElement('script');
    script.src = url.href;
    script.defer = true;
    script.addEventListener('load', () => { attempts = 0; awaitApp(); });
    document.body.append(script);
  }

  function icon() {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('width', '18');
    svg.setAttribute('height', '18');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    const path = document.createElementNS(svg.namespaceURI, 'path');
    path.setAttribute('d', 'M4 6h16v12H4z M4 7l8 6 8-6');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '1.8');
    path.setAttribute('stroke-linejoin', 'round');
    svg.append(path);
    return svg;
  }

  function enhance(button) {
    ensureApp();
    if (!ready() || !/^\d+$/.test(button.dataset.bnCardRestock || '') || !button.dataset.bnRestockUrl ||
        button.hasAttribute('data-bn-restock-ready')) return;
    const attributes = {};
    for (const name of ['aria-label', 'aria-haspopup']) attributes[name] = button.getAttribute(name);
    originals.set(button, {
      nodes: [...button.childNodes].map(node => node.cloneNode(true)),
      className: button.className,
      disabled: button.disabled,
      attributes,
    });
    const text = document.createElement('span');
    text.textContent = label;
    button.replaceChildren(icon(), text);
    button.disabled = false;
    button.classList.remove('disabled');
    button.setAttribute('aria-label', `${label}: ${button.dataset.bnRestockTitle || ''}`.trim());
    button.setAttribute('aria-haspopup', 'dialog');
    button.setAttribute('data-bn-restock-ready', '');
  }

  function scan(root) {
    if (root.nodeType !== 1 && root !== document) return;
    if (root.matches && root.matches(selector)) enhance(root);
    root.querySelectorAll(selector).forEach(enhance);
  }

  // Readiness polling is bounded; STOQ's event covers later asynchronous loads.
  function awaitApp() {
    clearTimeout(timer);
    if (!enabled) {
      document.querySelectorAll('[data-bn-restock-ready]').forEach(restore);
      return;
    }
    ensureApp();
    if (ready()) scan(document);
    else if (++attempts < 40) timer = setTimeout(awaitApp, 250);
  }
  window.BNCardRestock = { enhance, restore };
  window.addEventListener('stoq:loaded', event => {
    enabled = !(event.detail && event.detail.enabled === false);
    attempts = 0;
    awaitApp();
  });
  // Product recommendations and other cards arrive after load.
  new MutationObserver(records => {
    if (!enabled || !document.querySelector(selector)) return;
    ensureApp();
    if (!ready()) return;
    records.forEach(record => record.addedNodes.forEach(scan));
  }).observe(document.body, { childList: true, subtree: true });

  async function open(button) {
    if (pending.has(button)) return;
    pending.add(button);
    button.setAttribute('aria-busy', 'true');
    const url = new URL(button.dataset.bnRestockUrl, location.href);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      if (url.origin !== location.origin || !/\/products\/[^/]+\/?$/.test(url.pathname)) throw new Error('Unverified product URL');
      const endpoint = new URL(url.href);
      endpoint.pathname = endpoint.pathname.replace(/\/$/, '') + '.js';
      endpoint.search = '';
      endpoint.hash = '';
      const response = await fetch(endpoint.href, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
      if (!response.ok) throw new Error('Product could not be verified');
      const product = await response.json();
      if (String(product.id) !== button.dataset.bnCardRestock || product.available !== false ||
          !Array.isArray(product.variants) || !product.variants.length ||
          !product.variants.every(variant => variant.available === false && /^\d+$/.test(String(variant.id))) || !ready()) {
        throw new Error('Availability changed or restock app unavailable');
      }
      // Pass every variant so STOQ offers the right size or flavour picker.
      await window._RestockRocket.openModal(product, String(product.variants[0].id));
    } catch (_) {
      // An app or network failure, or stock that has come back, leads to the product page.
      location.assign(url.href);
    } finally {
      clearTimeout(timeout);
      pending.delete(button);
      button.removeAttribute('aria-busy');
    }
  }

  // Capture phase: runs before Empire's or a card form's own handlers, and
  // leaves modified clicks alone.
  document.addEventListener('click', event => {
    const button = event.target.closest && event.target.closest('[data-bn-restock-ready]');
    if (!button || !ready() || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    open(button);
  }, true);
  awaitApp();
})();
